import { ReportPdfData } from '../types';
import { createEyebrow, createFooter } from '../utils';
import { PdfAssets } from '../../pdfTemplates';

/**
 * Creates the source nodes page for the PDF report
 * @param data Report data
 * @param assets Logo and font assets for the PDF
 * @param colors Color palette for styling
 * @param pageNumber Current page number
 * @param totalPages Total number of pages
 * @returns Source nodes page content for pdfmake
 */
export const createSourceNodesPage = (
  data: ReportPdfData,
  assets: PdfAssets | undefined,
  colors: Record<string, string>,
  pageNumber: number,
  totalPages: number
) => {
  // Define source node types
  const allSourceTypes = ['mobile-sdk', 'javascript-source', 'server-source'];
  
  // Ensure selectedSections is an array
  const selectedSections = Array.isArray(data.selectedSections) ? data.selectedSections : [];
  
  // If SOURCES is selected, include all source types
  let sourceTypes = [];
  if (selectedSections.includes('SOURCES')) {
    sourceTypes = [...allSourceTypes];
  } else {
    // Otherwise, filter based on specific selections
    sourceTypes = allSourceTypes.filter(type => 
      selectedSections.includes(`SOURCES:${type}`) || 
      selectedSections.includes(type)
    );
  }
  
  // Check if there are any source nodes
  if (!data.architecture?.nodes || sourceTypes.length === 0) {
    return [];
  }
  
  const sourceNodes = data.architecture.nodes.filter(node => 
    sourceTypes.includes(node.type)
  );
  
  if (sourceNodes.length === 0) {
    return [];
  }
  
  // Group nodes by type
  const nodesByType: Record<string, any[]> = {};
  
  sourceNodes.forEach(node => {
    if (!nodesByType[node.type]) {
      nodesByType[node.type] = [];
    }
    nodesByType[node.type].push(node);
  });
  
  return [
    // Eyebrow section
    ...createEyebrow('Architecture Breakdown - Sources', assets?.markUrl, colors),
    
    // Content section
    {
      stack: Object.entries(nodesByType).map(([type, nodes]) => {
        // Get a friendly name for the source type
        let typeName = 'Source';
        let typeDescription = '';
        
        switch (type) {
          case 'mobile-sdk':
            typeName = 'Mobile App';
            typeDescription = 'The iOS SDK enables reliable data collection from your iOS applications, with support for Apple\'s privacy features and consistent tracking across all iOS devices.';
            break;
          case 'javascript-source':
            typeName = 'Website';
            typeDescription = 'The JavaScript SDK collects user interaction data from your websites and web applications, with support for modern web standards and privacy regulations.';
            break;
          case 'server-source':
            typeName = 'Server/API';
            typeDescription = 'The Server API allows you to send data directly from your backend systems, providing a reliable way to track server-side events and user actions.';
            break;
        }
        
        return {
          columns: [
            {
              stack: [
                {
                  text: typeName,
                  fontSize: 14,
                  bold: true,
                  color: colors.black,
                  margin: [0, 0, 0, 8]
                },
                {
                  text: typeDescription,
                  fontSize: 9,
                  color: colors.darkGrey,
                  margin: [0, 0, 0, 0]
                }
              ],
              width: '60%',
              margin: [0, 0, 12, 0]
            },
            {
              stack: nodes.map(node => ({
                stack: [
                  {
                    text: node.label,
                    fontSize: 9,
                    bold: true,
                    color: colors.black,
                    margin: [0, 0, 0, 8]
                  },
                  {
                    text: node.description || 'No description provided',
                    fontSize: 8,
                    color: colors.darkGrey,
                    margin: [0, 0, 0, 24]
                  },
                  // Add node-specific fields based on type
                  {
                    stack: [
                      {
                        text: 'Details:',
                        fontSize: 8,
                        bold: true,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 4]
                      },
                      // Only show fields that are specified
                      ...(node.url ? [{
                        text: 'URL: ' + node.url,
                        fontSize: 8,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 2]
                      }] : []),
                      
                      ...(node.eventVolume ? [{
                        text: 'Estimated monthly event volume: ' + node.eventVolume,
                        fontSize: 8,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 2]
                      }] : []),
                      
                      ...(node.eventSpec ? [{
                        text: 'Event Spec: ' + node.eventSpec,
                        fontSize: 8,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 2]
                      }] : []),
                      
                      ...(node.consentTool ? [{
                        text: 'Consent Management Tool: ' + node.consentTool,
                        fontSize: 8,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 2]
                      }] : []),
                      
                      ...(node.latencyImpact !== undefined && node.latencyImpact !== null ? [{
                        text: 'Latency Impact: ' + node.latencyImpact,
                        fontSize: 8,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 2]
                      }] : []),
                      
                      ...(type === 'mobile-sdk' && node.syncInjectorId ? [{
                        text: 'Sync Injector ID: ' + node.syncInjectorId,
                        fontSize: 8,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 2]
                      }] : []),
                      
                      ...(node.status ? [{
                        text: 'Status: ' + node.status,
                        fontSize: 8,
                        color: colors.darkGrey,
                        margin: [0, 0, 0, 2]
                      }] : [])
                    ],
                    margin: [0, 0, 0, 16]
                  }
                ]
              })),
              width: '40%',
              margin: [12, 0, 0, 0]
            }
          ],
          margin: [64, 48, 40, 32]
        };
      }),
    },
    
    // Footer
    createFooter(pageNumber, totalPages, colors),
    { text: '', pageBreak: 'after' }
  ];
};
